import { useGenErrorMsgBanners } from "@/utils/useGenErrorMsgBanners";
import axios from "axios";
import { useBannerContext } from "oolib";
import { useMutation, useQuery, useQueryClient } from "react-query";

export const useGetAllReports = (options = {}) => {
  const { enabled } = options;
  return useQuery(
    ["allReports"],
    () =>
      axios.get(`/api/reports/getAllReports`).then((res) => res.data),
    {
      enabled,
    }
  );
};

export const useViewReport = (options = {}) => {
  const { reportId, enabled } = options;
  return useQuery(
    ["reports", reportId],
    () => axios.get(`/api/reports/viewReport/${reportId}`).then((res) => res.data),
    {
      enabled,
    }
  );
};

export const useCreateReport = () => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ type, title, subtitle }) =>
      axios.post(
        `/api/reports/createReport/${type}`,
        {
          title, subtitle
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      ),
    {
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

export const useDeleteReport = () => {
  const { SET_INFO_BANNER } = useBannerContext();
  const queryClient = useQueryClient();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ reportId, title }) =>
      axios.delete(`/api/reports/deleteReport/${reportId}`),

    {
      onSuccess: (res, mutationArgs) => {
        console.log({mutationArgs})
        SET_INFO_BANNER({
          color: "green",
          msg: `Report: ${mutationArgs.title} deleted successfully!`,
          timeOut: 3000,
        });
        queryClient.invalidateQueries(["reports", mutationArgs.reportId]);
      },

      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

// Create/refresh a report's public "share for all to view" snapshot. Returns { token }.
// See SHARED_REPORTS_DESIGN.md.
export const useShareReportMutation = () => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ reportId, title, subtitle, config, snapshot }) =>
      axios
        .post(
          `/api/reports/shareReport/${reportId}`,
          { title, subtitle, config, snapshot },
          { headers: { "Content-Type": "application/json" } }
        )
        .then((res) => res.data),
    {
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

// Revoke a report's public link (enabled:false server-side).
export const useRevokeReportShare = () => {
  const { SET_INFO_BANNER } = useBannerContext();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ reportId }) =>
      axios.patch(`/api/reports/unshareReport/${reportId}`).then((res) => res.data),
    {
      onSuccess: () => {
        SET_INFO_BANNER({
          color: "green",
          msg: "Public link revoked.",
          timeOut: 3000,
        });
      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

// PUBLIC (unauthenticated): fetch a shared report snapshot by its link token.
// Returns { title, subtitle, config, snapshot, sharedAt }.
export const usePublicReport = (options = {}) => {
  const { token, enabled = true } = options;
  return useQuery(
    ["publicReport", token],
    () =>
      axios.get(`/api/reports/publicReport/${token}`).then((res) => res.data),
    {
      enabled: enabled && !!token,
      retry: false,
    }
  );
};

/**
 * Feature a report on the dashboard, or take it off. Its own endpoint, not
 * part of publishing: the flag is curation rather than content, and in
 * collab mode there is no publish step to carry it.
 */
export const useSetReportFeatured = () => {
  const queryClient = useQueryClient();
  const { SET_INFO_BANNER } = useBannerContext();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ reportId, featured }) =>
      axios.patch(`/api/reports/setReportFeatured/${reportId}`, { featured }, { headers: { "Content-Type": "application/json" } }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["allReports"]);
        SET_INFO_BANNER({
          color: "green",
          msg: res.data.featured ? "Featured on the dashboard." : "Removed from the dashboard.",
          timeOut: 3000,
        });
      },
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};

